import {
  canAuthorMultiMarket,
  isMultilangPocEnabled
} from '../../../constants/featureFlags'
import { SurveyState } from '../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../modules/Project/Project.hooks'
import { ProjectBreadcrumb } from '../ProjectBreadcrumb'
import {
  getMarkets,
  getMasterSurvey,
  isProjectInProgress
} from './markets.utils'

/**
 * The multi-market project flow steps, shown as a breadcrumb on every step
 * screen. Steps with a built page are navigable; the rest are rendered as
 * plain labels until their screens are wired in later steps.
 */
export const FLOW_STEPS = [
  'Project',
  'Audiences',
  'Survey',
  'Mapping',
  'Translations',
  'Confirm',
  'Fieldwork',
  'Results'
]

export const ProjectFlowBreadcrumb = ({
  activeStep
}: {
  activeStep: string
}) => {
  const projectId = useProjectId()
  const { project } = useGetProjectByProjectId()
  const masterSurvey = getMasterSurvey(project)

  // Every other step is closed to results-only viewers, so the nav is dead ends.
  if (!canAuthorMultiMarket()) {
    const projectName = project?.name ?? 'Project'
    return (
      <ProjectBreadcrumb
        activeStep={projectName}
        steps={[{ label: projectName }]}
        hideBack={true}
      />
    )
  }

  // Once any market has left Draft the shared master questionnaire is locked,
  // so the Survey step (which edits it) is removed from the flow entirely.
  // Until then the inverse applies to Fieldwork and Results: their data (quota
  // fill, collected completes, charts) only materializes once an order is
  // placed at launch, so pre-launch they are hidden instead of shown empty.
  const projectInProgress = isProjectInProgress(project)
  // Results defaults to Per Market on the first fielding market (Combined isn't
  // available yet); falls back to the Combined route if nothing is fielding.
  const firstFieldingSurveyId = getMarkets(project).find(
    (market) => market.state !== SurveyState.Draft
  )?.surveyId
  const steps = projectInProgress
    ? FLOW_STEPS.filter((step) => step !== 'Survey')
    : FLOW_STEPS.filter((step) => step !== 'Fieldwork' && step !== 'Results')

  // Only steps whose screens exist are navigable today.
  const pathByStep: Record<string, string | undefined> = {
    Project: `/project/${projectId}`,
    Audiences: `/project/${projectId}/audiences`,
    // The content steps only become navigable once the project has at least
    // one market (masterSurvey present). Survey, Mapping, Translations,
    // Confirm and Fieldwork all run over the shared project questionnaire, so
    // their routes are project-scoped (no surveyId).
    ...(masterSurvey && {
      Survey: `/project/${projectId}/survey`,
      Mapping: `/project/${projectId}/mapping`,
      // Fieldwork/Results are filtered out of `steps` pre-launch; keeping their
      // paths gated too makes the invariant explicit.
      ...(projectInProgress && {
        Fieldwork: `/project/${projectId}/fieldwork`,
        Results: firstFieldingSurveyId
          ? `/project/${projectId}/results/${firstFieldingSurveyId}`
          : `/project/${projectId}/results`
      }),
      // Translations and Confirm are gated by the same flag as the
      // translations editor.
      ...(isMultilangPocEnabled() && {
        Translations: `/project/${projectId}/translations`,
        Confirm: `/project/${projectId}/confirm`
      })
    })
  }

  return (
    <ProjectBreadcrumb
      activeStep={activeStep}
      steps={steps.map((label) => ({ label, path: pathByStep[label] }))}
    />
  )
}
